import React, { useMemo } from 'react'
import { Text, Box, SimpleGrid, IconButton, Flex } from '@chakra-ui/react'
import { DragHandleIcon } from '@chakra-ui/icons'
import useDispatch from '~editor/hooks/useDispatch'
import { BinIcon, CopyIcon } from '~editor/components/icons'
import * as ActionsComponents from './ActionsComponents'
import { useUserAbilities } from '~editor/hooks/useUser'
import {
  ActionTypes,
  ActionTypesMap,
  AnyActionType,
  getActionConstructor,
  getMeta,
  IActionComponentProps,
  KeybindAction,
} from '~core/forge.type'

const {
  KeybindActionComponent,
  DelayActionComponent,
  MouseWheelActionComponent,
  MouseButtonActionComponent,
  MouseMoveActionComponent,
  PlayActionComponent,
  GoToURLActionComponent,
  GoToShardActionComponent,
  GoToForgeActionComponent,
  TypeStringActionComponent,
  ToggleStateActionComponent,
  SetStateActionComponent,
  SetVisibilityActionComponent,
  ScreenshotActionComponent,
} = ActionsComponents

type ActionComponent = typeof ActionsComponents[keyof typeof ActionsComponents]

export const ActionBase = (props: IActionComponentProps) => {
  const { actions, activeActionGesture, isInspectorPanel } = props
  let { action } = props
  const dispatch = useDispatch()
  const Meta = getMeta(action)
  const actionConstructor = getActionConstructor(action)
  const abilities = useUserAbilities()

  if (actionConstructor === KeybindAction && isInspectorPanel) {
    action = {
      ...action,
      typeLocked: true,
    } as KeybindAction
  }

  const canEditActions = useMemo(
    () => abilities?.can('edit', 'NodeActions'),
    [abilities],
  )
  const canMoveActions = useMemo(
    () => abilities?.can('move', 'NodeActions'),
    [abilities],
  )
  const canDuplicateActions = useMemo(
    () => abilities?.can('duplicate', 'NodeActions'),
    [abilities],
  )
  const canDeleteActions = useMemo(
    () => abilities?.can('delete', 'NodeActions'),
    [abilities],
  )

  return (
    <SimpleGrid
      templateRows={'repeat(1)'}
      templateColumns={!isInspectorPanel ? '1fr auto' : '1fr'}
      gap="8px"
      alignItems="center"
      justifyContent="flex-start"
      paddingBottom="10px"
      fontSize={!isInspectorPanel ? '12px' : '11px'}
      opacity={canEditActions ? 1 : 0.51}
      position="relative"
      _before={
        canEditActions
          ? {}
          : {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              cursor: 'not-allowed',
            }
      }
    >
      <Flex
        flexDirection={isInspectorPanel ? 'column' : 'row'}
        flexGrow={1}
        alignItems={isInspectorPanel ? 'flex-start' : 'center'}
        padding="5px"
        paddingLeft={!isInspectorPanel ? undefined : '10px'}
        background="ui.backgrounds.light"
        borderRadius="5px"
        gap="5px"
      >
        <SimpleGrid
          flexGrow={isInspectorPanel ? 0 : 1}
          justifyContent="flex-start"
          alignItems="center"
          whiteSpace="nowrap"
          templateColumns={!isInspectorPanel ? 'auto auto 1fr' : 'auto 1fr'}
          gap="10px"
        >
          {!isInspectorPanel && (
            <DragHandleIcon
              opacity={canMoveActions ? 1 : 0}
              name="drag-handle"
              fontSize={'inherit'}
              color="ui.text.grey"
            />
          )}
          <Meta.icon color="ui.text.grey" />
          <Text flex={1} fontWeight="bold" fontSize={'inherit'}>
            {Meta.label}
            {getActionConstructor(action) === KeybindAction &&
              isInspectorPanel &&
              ` (${action.type?.replace('key', '')})`}
          </Text>
        </SimpleGrid>

        <Box display="flex" justifyContent="flex-start">
          {(() => {
            let ActionComponent: ActionComponent | null = null
            const actionComponentProps = {
              action,
              actions,
              activeActionGesture,
              isInspectorPanel,
              isDisabled: !canEditActions,
            } as Parameters<ActionComponent>[0]

            switch (action.type) {
              case ActionTypes.goToURL: {
                ActionComponent = GoToURLActionComponent
                break
              }
              case ActionTypes.play: {
                ActionComponent = PlayActionComponent
                break
              }
              case ActionTypes.delay: {
                ActionComponent = DelayActionComponent
                break
              }

              case ActionTypes.mouseWheel: {
                ActionComponent = MouseWheelActionComponent
                break
              }

              case ActionTypes.mouseClick:
              case ActionTypes.mouseDown:
              case ActionTypes.mouseUp: {
                ActionComponent = MouseButtonActionComponent
                break
              }

              case ActionTypes.mouseMoveRelative:
              case ActionTypes.mouseMoveAbsolute: {
                ActionComponent = MouseMoveActionComponent
                break
              }

              case ActionTypes.keyTap:
              case ActionTypes.keyDown:
              case ActionTypes.keyUp: {
                ActionComponent = KeybindActionComponent
                break
              }

              case ActionTypes.goToShard: {
                ActionComponent = GoToShardActionComponent
                break
              }

              case ActionTypes.goToForge: {
                ActionComponent = GoToForgeActionComponent
                break
              }

              case ActionTypes.typeString: {
                ActionComponent = TypeStringActionComponent
                break
              }

              case ActionTypes.toggleState: {
                ActionComponent = ToggleStateActionComponent
                break
              }

              case ActionTypes.setVisibility: {
                ActionComponent = SetVisibilityActionComponent
                break
              }

              case ActionTypes.setState: {
                ActionComponent = SetStateActionComponent
                break
              }

              case ActionTypes.screenshot: {
                ActionComponent = ScreenshotActionComponent
                break
              }

              default: {
                return <span>Action component missing</span>
              }
            }

            if (ActionComponent) {
              return <ActionComponent {...(actionComponentProps as any)} />
            }
          })()}
        </Box>

        {!isInspectorPanel && canDuplicateActions && (
          <IconButton
            aria-label="duplicate-action"
            variant="icon"
            icon={<CopyIcon />}
            onClick={() => {
              const { id, ...actionProps } = action
              const _action = new ActionTypesMap[action.type]()

              Object.assign(_action, actionProps)

              dispatch.nodes.updateActions({
                actionGestureType: activeActionGesture,
                actions: actions.reduce((acc, a) => {
                  acc.push(a)

                  if (action.id === a.id) {
                    acc.push(_action)
                  }

                  return acc
                }, [] as AnyActionType[]),
              })
            }}
          />
        )}
      </Flex>

      {!isInspectorPanel && canDeleteActions && (
        <IconButton
          aria-label="delete-action"
          variant="iconWarning"
          icon={<BinIcon />}
          onClick={() => {
            dispatch.nodes.updateActions({
              actionGestureType: activeActionGesture,
              actions: actions.filter((_action: AnyActionType) => {
                return action.id !== _action.id
              }),
            })
          }}
        />
      )}
    </SimpleGrid>
  )
}

export default ActionBase
